/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useState
} from 'react';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {getGroup} from '@ion/core/src/services/Connect';
import {
  GenericObject,
  Group
} from '@ion/core/src/types/entities';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import {
  GroupAssignmentDetailsFields,
  isAdd,
  isRemove
} from './GroupAssignmentDetailsForm';
import {GroupAssignmentActions} from './types';

export interface UseGroupAssignmentChooserResult {
  groupAction?: GroupAssignmentActions;
  handleActionChooser?: MouseEventHandler;
  isNewGroup?: boolean;
  setReplaceGroups?: Function;
  replaceGroups?: GenericObject[];
  handleRemoveGroupChooser?: (group: Group) => void;
  handleFromGroupChooser?: (group: Group) => void;
  fromGroup?: GenericObject;
  toGroup?: GenericObject;
  handleAccountGroupChooser?: (group: Group) => void;
  handleGroupChooser?: (group: Group, multiSelect: boolean, isNewGroup: boolean) => void;
  isGroupNameEmpty?: boolean;
  isGroupNameExists?: boolean;
  groupName?: string;
  groupNotes?: string;
  onAction?: Function;
  setNoGroups?: Function;
  noGroups?: boolean;
  setShowGroupsError?: Function;
  showGroupsError?: boolean;
  setFormState?: (val: FormState) => void;
  formState?: GenericObject;
  fromAccount?: GenericObject;
  handleGroupNameChooser?: (value: string) => void;
  handleGroupNotesChooser?: (value: string) => void;
  setIsGroupEmpty?: (isGroupEmpty: boolean) => void;
  isGroupEmpty?: boolean;
  setIsFromGroupEmpty?: (isFromGroupEmpty: boolean) => void;
  isFromGroupEmpty?: boolean;
}

export interface UseGroupAssignmentChooserProps {
  setTaskNickNameAction: (action: string) => void
}

/**
 * Re-usable hook for managing the actions of group assignment container.
 */
export const useGroupAssignmentChooser = ({
  setTaskNickNameAction
}: UseGroupAssignmentChooserProps): UseGroupAssignmentChooserResult => {
  const [groupAction, setGroupAction]             = useState<GroupAssignmentActions>(GroupAssignmentActions.Add);
  const [isNewGroup, setIsNewGroup]               = useState<boolean>(false);
  const [fromGroup, setFromGroup]                 = useState(null);
  const [fromAccount, setFromAccount]             = useState(null);
  const [toGroup, setToGroup]                     = useState(null);
  const [groupName, setGroupName]                 = useState<string>('');
  const [groupNotes, setGroupNotes]               = useState<string>('');
  const [isGroupNameEmpty, setIsGroupNameEmpty]   = useState<boolean>(false);
  const [isGroupNameExists, setIsGroupNameExists] = useState<boolean>(false);
  const [noGroups, setNoGroups]                   = useState<boolean>(false);
  const [showGroupsError, setShowGroupsError]     = useState<boolean>(false);
  const [replaceGroups, setReplaceGroups]         = useState([]);
  const [formState, setFormState]                 = useFormState();
  const [isGroupEmpty, setIsGroupEmpty]           = useState<boolean>(false);
  const [isFromGroupEmpty, setIsFromGroupEmpty]   = useState<boolean>(false);

  const ADD_NEW_GROUP = translate('common:editors.bulkActions.groupAssignment.newGroup');
  const REMOVE_ALL_GROUP = translate('common:editors.bulkActions.groupAssignment.removeAllGroup');

  useEffect(() => {
    const action: string = getTaskNickNameAction({groupAction, formState});
    setTaskNickNameAction(action);
  }, [groupAction, formState]);

  useEffect(() => {
    handleFormState(GroupAssignmentDetailsFields.ADD_GROUP, false, '');
    handleFormState(GroupAssignmentDetailsFields.REPLACE_TO_GROUP, false, '');
    setIsNewGroup(false);
  }, [groupAction]);

  const handleActionChooser = useCallback((value: GroupAssignmentActions) => {
    setGroupAction(value);
    resetNewGroupFields();
  }, []);

  const resetNewGroupFields = useCallback(() => {
    setGroupName('');
    setGroupNotes('');
    setIsGroupNameEmpty(false);
    setIsGroupNameExists(false);
    handleFormState(GroupAssignmentDetailsFields.NAME, false, '');
    handleFormState(GroupAssignmentDetailsFields.NOTES, false, '');
  }, []);

  const handleRemoveGroupChooser = useCallback((value, multiSelect, isRemoveAllGroups) => {
    if (multiSelect && isRemoveAllGroups) {
      value.push({
        name : REMOVE_ALL_GROUP
      });
    }

    handleFormState(GroupAssignmentDetailsFields.REMOVE_GROUP, true, value);
  }, []);

  const handleFromGroupChooser = useCallback(async(value) => {
    setFromGroup(value);
    const groupData = await getGroup(value?.id);
    setFromAccount(groupData?.account);
    handleFormState(GroupAssignmentDetailsFields.REPLACE_FROM_GROUP, true, value);
  }, [setFromAccount]);

  const handleAccountGroupChooser = useCallback((value) => {
    handleFormState(GroupAssignmentDetailsFields.ACCOUNT, true, value);
    setIsNewGroup(false);
  }, []);

  const handleGroupChooser = useCallback((value, multiSelect, isNewGroup) => {
    setIsNewGroup(multiSelect ? isNewGroup : !value);
    if (!multiSelect && isUndefined(value)) {
      value = {
        name : ADD_NEW_GROUP
      };
    } else if (multiSelect && isNewGroup) {
      value.push({
        name : ADD_NEW_GROUP
      });
    }
    if (isAdd(groupAction)) {
      handleFormState(GroupAssignmentDetailsFields.ADD_GROUP, true, value);
      handleFormState(GroupAssignmentDetailsFields.REPLACE_TO_GROUP, false, '');
    } else {
      setToGroup(value);
      handleFormState(GroupAssignmentDetailsFields.REPLACE_TO_GROUP, true, value);
      handleFormState(GroupAssignmentDetailsFields.ADD_GROUP, false, '');
      resetNewGroupFields();
    }
  }, [groupAction]);

  const handleGroupNameChooser = useCallback((value) => {
    setGroupName(value);
    handleFormState(GroupAssignmentDetailsFields.NAME, true, value);
  }, []);

  const handleGroupNotesChooser = useCallback((value) => {
    setGroupNotes(value);
    handleFormState(GroupAssignmentDetailsFields.NOTES, true, value);
  }, []);

  const handleFormState = useCallback((name, required, value) => {
    setFormState({
      [name] : {
        name     : name,
        required : required,
        value
      }
    });
    setIsGroupEmpty(false);
    setIsFromGroupEmpty(false);
  }, [setFormState]);

  return {
    groupAction,
    handleActionChooser,
    isNewGroup,
    setReplaceGroups,
    replaceGroups,
    handleRemoveGroupChooser,
    handleFromGroupChooser,
    fromGroup,
    toGroup,
    handleAccountGroupChooser,
    handleGroupChooser,
    isGroupNameEmpty,
    isGroupNameExists,
    groupName,
    groupNotes,
    setNoGroups,
    noGroups,
    setShowGroupsError,
    showGroupsError,
    setFormState,
    formState,
    fromAccount,
    handleGroupNameChooser,
    handleGroupNotesChooser,
    setIsGroupEmpty,
    isGroupEmpty,
    setIsFromGroupEmpty,
    isFromGroupEmpty
  };
};

const getGroupName = (groups: Group[], newGroup: string) => (
  groups
    ?.filter(group => group.name !== newGroup)
    .map(group => group.name)
    .join(',')
);

const isMultipleGroupsSelected = (action, formState): boolean => (
  Array.isArray(formState[action]?.value) && formState[action]?.value?.length > 0
);

/**
 * Function to build the action string to be inserted into the overall task nickname.
 *
 * If the current group action is "add", the action is "- Add to {account}/{group}",
 * where {group} is either an existing group in the current {account}, or a new group
 * to be created.
 *
 * If the current group action is "remove", the action is "- Remove from {group}",
 * where {group} is an existing group in the current account.
 *
 * If the current group action is "replace", the action is "- Replace from {oldGroup}
 * to {account}/{newGroup}", where {oldGroup} is an existing group in the current
 * account, and {newGroup} is either an existing group in the current {account}, or a
 * new group to be created.
 */
const getTaskNickNameAction = ({groupAction, formState}): string => {
  const {
    ACCOUNT,
    ADD_GROUP,
    REMOVE_GROUP,
    REPLACE_FROM_GROUP,
    REPLACE_TO_GROUP,
    NAME
  } = GroupAssignmentDetailsFields;
  const accountName          = formState[ACCOUNT]?.value?.title;
  const path                 = 'common:editors.bulkActions.groupAssignment';
  const ADD_NEW_GROUP        = translate(`${path}.newGroup`);
  const REMOVE_ALL_GROUP     = translate(`${path}.removeAllGroup`);
  const addGroupName         = isMultipleGroupsSelected(ADD_GROUP, formState)
    ? getGroupName(formState[ADD_GROUP]?.value, ADD_NEW_GROUP) : formState[ADD_GROUP]?.value?.name;
  const removeGroupName      = isMultipleGroupsSelected(REMOVE_GROUP, formState)
    ? getGroupName(formState[REMOVE_GROUP]?.value, REMOVE_ALL_GROUP) : formState[REMOVE_GROUP]?.value?.name;
  const replaceFromGroupName = formState[REPLACE_FROM_GROUP]?.value?.name;
  const replaceToGroupName   = formState[REPLACE_TO_GROUP]?.value?.name;
  const newGroupName         = formState[NAME]?.value;
  let name: string;
  if (isAdd(groupAction) && accountName?.length) {
    name = accountName;
    if (isMultipleGroupsSelected(ADD_GROUP, formState)) {
      if (newGroupName?.length) {
        name = addGroupName?.length ? `${accountName}/${newGroupName},${addGroupName}` : `${accountName}/${newGroupName}`;
      } else {
        name = `${accountName}/${addGroupName}`;
      }
    } else if (addGroupName?.length && addGroupName !== ADD_NEW_GROUP) {
      name = `${accountName}/${addGroupName}`;
    }
    return `- ${translate(`${path}.addToGroupAction`, {group: name})}`;
  } else if (isRemove(groupAction)) {
    let removeName = false;
    if (isMultipleGroupsSelected(REMOVE_GROUP, formState) &&
      formState[REMOVE_GROUP]?.value.find(({name}) => name.includes(REMOVE_ALL_GROUP))) {
      removeName = true;
    }
    return `- ${translate(`${path}.removeFromGroupAction`, {group: removeName ? 'All Groups' : removeGroupName})}`;
  } else if (replaceFromGroupName?.length && accountName?.length) {
    name = accountName;
    if (newGroupName?.length) {
      name = `${accountName}/${newGroupName}`;
    } else if (replaceToGroupName?.length && replaceToGroupName !== ADD_NEW_GROUP) {
      name = `${accountName}/${replaceToGroupName}`;
    }
    return `- ${translate(`${path}.replaceGroupAction`, {fromGroup: replaceFromGroupName, toGroup: name})}`;
  }
  return '';
};
